import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Strikethrough,
  Underline,
} from "lucide-react";
import { useRef } from "react";
import { cn } from "../lib/utils";
import {
  toggleUnicodeBold,
  toggleUnicodeItalic,
  toggleUnicodeStrikethrough,
  toggleUnicodeUnderline,
} from "../lib/richCaption";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

type RichCaptionTextareaProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  className?: string;
  textareaClassName?: string;
};

export function RichCaptionTextarea({
  id,
  value,
  onChange,
  placeholder,
  maxLength,
  className,
  textareaClassName,
}: RichCaptionTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const replaceSelection = (
    transform: (selected: string) => string,
    fallbackText?: string
  ) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = value.slice(start, end);
    const source = selected || fallbackText || "";
    if (!source) return;

    const transformed = transform(source);
    const transformedValue = `${value.slice(0, start)}${transformed}${value.slice(end)}`;
    const next =
      maxLength === undefined
        ? transformedValue
        : transformedValue.slice(0, maxLength);
    onChange(next);

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start, start + transformed.length);
    });
  };

  const applyBulletedList = () => {
    replaceSelection(
      (selected) =>
        selected
          .split("\n")
          .map((line) =>
            line.trim()
              ? `• ${line.replace(/^\s*([•-]|\d+\.)\s*/, "")}`
              : ""
          )
          .join("\n"),
      "List item"
    );
  };

  const applyNumberedList = () => {
    replaceSelection(
      (selected) =>
        selected
          .split("\n")
          .map((line, index) =>
            line.trim()
              ? `${index + 1}. ${line.replace(/^\s*([•-]|\d+\.)\s*/, "")}`
              : ""
          )
          .join("\n"),
      "List item"
    );
  };

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => replaceSelection(toggleUnicodeBold)}
          aria-label="Bold"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => replaceSelection(toggleUnicodeItalic)}
          aria-label="Italic"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => replaceSelection(toggleUnicodeUnderline)}
          aria-label="Underline"
        >
          <Underline className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => replaceSelection(toggleUnicodeStrikethrough)}
          aria-label="Strikethrough"
        >
          <Strikethrough className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={applyBulletedList}
          aria-label="Bulleted list"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={applyNumberedList}
          aria-label="Numbered list"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
      </div>

      <Textarea
        id={id}
        ref={textareaRef}
        placeholder={placeholder}
        maxLength={maxLength}
        className={cn("notranslate", textareaClassName)}
        translate="no"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
